import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnChangeValue } from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { escapeRegExp, uniqBy } from 'lodash';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  Contact,
  ContactWhereInput,
  Query,
  QueryContactsArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { FlexColumn } from 'src/components/domain/contacts/contact-details/shared';
import { ContactStageIcon } from 'src/components/domain/contacts/contact-stage-icon';
import {
  GET_CONTACTS,
  UseContacts,
} from 'src/components/domain/contacts/hooks/use-contacts';
import { UsersRefetchType } from 'src/components/domain/users/hooks/use-company-users';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { FlexRow } from 'src/components/layout/flex-row';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

const OptionContactStageIcon = styled(ContactStageIcon)`
  height: 30px;
  width: 30px;

  .contact-stage-icon_icon {
    svg {
      width: 15px;
    }
  }

  ${up('md')} {
    height: 25px;
    width: 25px;

    .contact-stage-icon_icon {
      svg {
        width: 13px;
      }
    }
  }
`;

const ValueContactStageIcon = styled(ContactStageIcon)`
  height: 22px;
  width: 22px;

  .contact-stage-icon_icon {
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const ValueLabel = styled.span`
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 16px;
`;

const OptionLabel = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  line-height: 19px;

  ${up('md')} {
    font-size: 12px;
    line-height: 15px;
  }
  .bolded-text {
    font-family: ${({ theme }) => theme.font.medium};
    font-weight: 500;
    font-size: 12px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0px;
    text-decoration: underline;
  }
`;

const OptionSubTitle = styled.div`
  color: ${({ theme }) => theme.colors.geyser};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 16px;

  ${up('md')} {
    font-size: 10px;
    line-height: 13px;
  }

  .bolded-text {
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 10px;
    leading-trim: NONE;
    line-height: 13px;
    letter-spacing: 0px;
    text-decoration: underline;
  }
`;

type ContactSelectOption = {
  label: string;
  subTitle?: string;
  value: number;
  contact: Contact;
};

type CompanyContactsSelectProps<IsMulti extends boolean> = {
  selectedContactIds: number[];
  isMulti: IsMulti;
  isClearable?: boolean;
  forceCaret?: boolean;
  onChange: ValueChanged<OnChangeValue<SelectOption<number>, IsMulti>>;
  isCreatable?: boolean;
  name: string;
  placeholder?: string;
  mobileLabel?: string;
  label?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  refetchRef?: (refetch: UsersRefetchType) => void;
  triggerOverride?: (
    value: OnChangeValue<SelectOption<number>, IsMulti>,
  ) => React.ReactNode;
  hideIndicators?: boolean;
};

const highlightMatch = (text: string, query?: string): React.ReactNode => {
  if (!query) return text;

  // Case-insensitive match; a single capture group so split() interleaves
  // the matched substrings into the result at odd indices.
  const regex = new RegExp(`(${escapeRegExp(query.trim())})`, 'i');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="bolded-text">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const OptionContainer = styled(FlexRow)`
  height: 42px;
  align-items: center;

  ${up('md')} {
    height: 32px;
  }
`;

const HighlightSubtitleText = ({ fullText, keyword }) => {
  if (!keyword) return <span>{fullText}</span>;

  const parts = fullText.split(new RegExp(`(${keyword})`, 'gi'));

  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <strong key={index}>{part}</strong>
        ) : (
          part
        ),
      )}
    </span>
  );
};

export const CompanyContactsSelect = <IsMulti extends boolean>(
  props: CompanyContactsSelectProps<IsMulti>,
) => {
  const {
    isMulti,
    selectedContactIds,
    isLoading,
    isCreatable,
    isDisabled,
    isClearable = true,
    forceCaret = false,
    hasError = false,
    refetchRef,
    triggerOverride,
    mobileLabel,
    hideIndicators = false,
    ...rest
  } = props;

  const { t } = useTranslation(['common']);
  const getMatchingSubTitle = useCallback(
    (contact: Contact, searchQuery: string) => {
      const {
        primaryPhone,
        secondaryPhone,
        fullName,
        id,
        label,
        primaryEmail,
        interest,
      } = contact;
      const defaultReturn = `ID: ${id}`;

      return match(searchQuery.toLowerCase())
        .when(
          (query) => label?.toLowerCase().includes(query),
          () => defaultReturn,
        )
        .when(
          (query) => fullName?.toLowerCase().includes(query),
          () => fullName,
        )
        .when(
          (query) => primaryEmail?.toLowerCase().includes(query),
          () => primaryEmail,
        )
        .when(
          (query) => primaryPhone?.national?.toLowerCase().includes(query),
          () => primaryPhone?.national,
        )
        .when(
          (query) => secondaryPhone?.national?.toLowerCase().includes(query),
          () => secondaryPhone?.national,
        )
        .when(
          (query) => interest?.toLowerCase().includes(query),
          () => `${t('common:interest')}: ${interest}`,
        )
        .otherwise(() => defaultReturn);
    },
    [t],
  );

  const contactToSelectOptionMapper = useCallback(
    (contact: Contact, searchQuery: string) => ({
      label: contact.label,
      subTitle: getMatchingSubTitle(contact, searchQuery)?.toString(),
      value: contact.id,
      contact,
    }),
    [getMatchingSubTitle],
  );

  const currentInstitution = useCurrentInstitution();
  const [fetchExtraContacts] = useLazyQuery<
    Pick<Query, 'contacts'>,
    Partial<QueryContactsArgs>
  >(GET_CONTACTS, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const whereInput: ContactWhereInput = useMemo(() => {
    return {
      _or: [
        {
          interest: {
            _contains: searchQuery,
          },
        },
        {
          matchKeywords: { _all: searchQuery },
        },
      ],
    };
  }, [searchQuery]);

  const {
    contacts,
    loading: loadingContacts,
    refetch,
    loadMore,
    hasNextPage,
  } = UseContacts(whereInput);

  const options = useMemo(() => {
    return contacts.map((contact) => contactToSelectOptionMapper(contact, searchQuery));
  }, [contactToSelectOptionMapper, contacts, searchQuery]);

  const [cachedContactOptions, setCachedContactOptions] = useState<ContactSelectOption[]>(
    [],
  );

  useEffect(() => {
    if (options.length) {
      setCachedContactOptions((prevState) => {
        return uniqBy([...prevState, ...options], 'value');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingContacts]);

  // Tracks ids we've already tried to fetch (found or not), so a selected id that
  // doesn't resolve to a contact (e.g. deleted) doesn't get re-fetched on every render.
  const attemptedContactIdsRef = useRef<Set<number>>(new Set());

  // Whenever a selected id isn't part of cachedContactOptions yet (initial load, or the
  // selection changed to something the search/pagination hasn't surfaced), fetch it
  // explicitly so `value` can still resolve it.
  useEffect(() => {
    const missingIds = selectedContactIds.filter(
      (id) =>
        !cachedContactOptions.some((option) => option.value === id) &&
        !attemptedContactIdsRef.current.has(id),
    );

    if (!missingIds.length) {
      return;
    }

    missingIds.forEach((id) => attemptedContactIdsRef.current.add(id));

    void (async () => {
      const { data } = await fetchExtraContacts({
        variables: {
          institutionId: currentInstitution.id,
          where: {
            id: { _in: missingIds },
          },
        },
      });

      const nodes = getNodesFromConnection(data?.contacts);

      if (nodes.length) {
        setCachedContactOptions((prevState) => {
          return uniqBy(
            [...prevState, ...nodes.map((node) => contactToSelectOptionMapper(node, ''))],
            'value',
          );
        });
      }
    })();
  }, [
    cachedContactOptions,
    contactToSelectOptionMapper,
    currentInstitution.id,
    fetchExtraContacts,
    selectedContactIds,
  ]);

  const value = useMemo(
    () =>
      selectedContactIds
        .map((id) => cachedContactOptions.find((option) => option.value === id))
        .filter(Boolean) as ContactSelectOption[],
    [cachedContactOptions, selectedContactIds],
  );

  return (
    <Select
      {...rest}
      isSearchable
      isMulti={isMulti}
      onChange={(value) => {
        rest?.onChange(value);
      }}
      onMenuClose={async () => {
        setSearchQuery('');
        await refetch({
          where: {},
        });
      }}
      onSearch={async (searchQuery) => {
        setSearchQuery(searchQuery);
      }}
      triggerOverride={triggerOverride}
      mobileLabel={mobileLabel}
      isClearable={isClearable}
      forceCaret={forceCaret}
      canLoadMore={hasNextPage}
      onLoadMore={async () => {
        await loadMore();
      }}
      isLoading={loadingContacts || isLoading}
      isCreatable={isCreatable}
      isDisabled={isDisabled}
      hasError={hasError}
      hideIndicators={hideIndicators}
      value={value}
      options={options}
      renderOption={(option) => {
        const contact = option['contact'] as Contact;

        if (!contact) {
          return null;
        }

        return (
          <OptionContainer>
            <OptionContactStageIcon
              slug={contact.stage?.slug}
              name={contact.stage?.name}
            />
            <FlexColumn>
              <OptionLabel>{highlightMatch(option.label, searchQuery)}</OptionLabel>
              {option.subTitle ? (
                <OptionSubTitle>
                  <HighlightSubtitleText
                    fullText={option.subTitle}
                    keyword={searchQuery}
                  />
                </OptionSubTitle>
              ) : null}
            </FlexColumn>
          </OptionContainer>
        );
      }}
      disableInternalSearch
      searchPlaceholder="Search for contact name, email, phone, interest or IDs"
      placeholder="Select related contact"
      renderValue={(value) => {
        const contact = value['contact'] as Contact;

        if (!contact) {
          return null;
        }

        return (
          <FlexRow style={{ maxHeight: '32px', alignItems: 'center' }}>
            <ValueContactStageIcon
              slug={contact.stage?.slug}
              name={contact.stage?.name}
            />
            <ValueLabel>{value.label}</ValueLabel>
          </FlexRow>
        );
      }}
    />
  );
};
